import React, { createContext, useContext, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { BASE_URL, DEMO_MODE } from '../services/api';
import {
  DEMO_CREDENTIALS,
  DEMO_USER,
  clearDemoSession,
  createDemoSession,
  isDemoToken,
  readDemoSession,
} from '../services/demoData';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(null);
  const navigate = useNavigate();

  // Check for existing token on mount
  useEffect(() => {
    const storedToken = localStorage.getItem('stockhub_token');
    if (storedToken) {
      setToken(storedToken);
      // Verify token and get user info
      verifyToken(storedToken);
    } else {
      setLoading(false);
    }
  }, []);

  const verifyToken = async (tokenToVerify) => {
    try {
      if (DEMO_MODE) {
        const session = readDemoSession();
        if (!session || !isDemoToken(tokenToVerify) || session.token !== tokenToVerify) {
          clearDemoSession();
          localStorage.removeItem('stockhub_token');
          setToken(null);
          setUser(null);
          setLoading(false);
          return;
        }

        setUser(session.user || DEMO_USER);
        setToken(session.token);
        setLoading(false);
        return;
      }

      const response = await fetch(`${BASE_URL}/auth/me`, {
        headers: {
          'Authorization': `Bearer ${tokenToVerify}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const userData = await response.json();
        setUser(userData);
        setToken(tokenToVerify);
      } else {
        // Token is invalid, remove it
        localStorage.removeItem('stockhub_token');
        setToken(null);
        setUser(null);
      }
    } catch (error) {
      console.error('Token verification failed:', error);
      localStorage.removeItem('stockhub_token');
      setToken(null);
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  const login = async (usernameOrEmail, password) => {
    try {
      if (DEMO_MODE) {
        if (
          usernameOrEmail === DEMO_CREDENTIALS.username &&
          password === DEMO_CREDENTIALS.password
        ) {
          const session = createDemoSession();
          localStorage.setItem('stockhub_token', session.token);
          setToken(session.token);
          setUser(session.user);
          return { success: true };
        } else {
          return {
            success: false,
            error: `Invalid demo credentials. Use ${DEMO_CREDENTIALS.username}/${DEMO_CREDENTIALS.password}`,
          };
        }
      }

      const response = await fetch(`${BASE_URL}/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          username_or_email: usernameOrEmail,
          password: password,
        }),
      });

      if (response.ok) {
        const data = await response.json();
        const { access_token } = data;
        
        // Store token
        localStorage.setItem('stockhub_token', access_token);
        setToken(access_token);
        
        // Get user info
        await verifyToken(access_token);
        
        return { success: true };
      } else {
        const errorData = await response.json();
        return { success: false, error: errorData.detail || 'Login failed' };
      }
    } catch (error) {
      console.error('Login error:', error);
      return { success: false, error: 'Network error. Please try again.' };
    }
  };

  const register = async (username, email, password) => {
    try {
      if (DEMO_MODE) {
        return {
          success: false,
          error: `Registration is disabled in demo mode. Sign in with ${DEMO_CREDENTIALS.username}/${DEMO_CREDENTIALS.password}.`,
        };
      }

      const response = await fetch(`${BASE_URL}/auth/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          username,
          email,
          password,
        }),
      });

      if (response.ok) {
        // Auto-login after successful registration
        const loginResult = await login(username, password);
        if (loginResult.success) {
          return { success: true };
        } else {
          return { success: false, error: 'Registration successful but login failed' };
        }
      } else {
        const errorData = await response.json();
        return { success: false, error: errorData.detail || 'Registration failed' };
      }
    } catch (error) {
      console.error('Registration error:', error);
      return { success: false, error: 'Network error. Please try again.' };
    }
  };

  const logout = () => {
    localStorage.removeItem('stockhub_token');
    clearDemoSession();
    setToken(null);
    setUser(null);
    navigate('/');
  };

  const isAuthenticated = () => {
    return !!token && !!user;
  };

  const getAuthHeaders = () => {
    if (token) {
      return {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      };
    }
    return {
      'Content-Type': 'application/json',
    };
  };

  const value = {
    user,
    token,
    loading,
    login,
    register,
    logout,
    isAuthenticated,
    getAuthHeaders,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
